// The 3D half of the eye test scene, posed from the SAME controller quaternion the lift uses.

import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { SparkRenderer } from '@sparkjsdev/spark'
import { SPARK_TRAINING_BLUR } from '../ogs/render/sparkBlur.js'
import { useFrame, useThree } from '@react-three/fiber'
import { EYE_SIDES, createEyeSystem } from '../ogs/eyes/index.ts'
import { quaternion } from '../ogs/math/index.ts'
import NeutralBranchMount from './NeutralBranchMount.jsx'
import { applyGazeRequest, eyeTestState } from './eyeTestStore.js'

const ARROW_LENGTH = 0.05
const ARROW_COLOR = { left: 0x4fd1ff, right: 0xffb347 }

// Per-frame scratch — a test scene is still no place to allocate 120 times a second.
const _direction = new THREE.Vector3()
const _cameraPosition = new THREE.Vector3()

// Prefers the GLB's own NORMAL: recomputing re-derives it from whatever winding survived.
function meshPartGeometry({ positions, indices, uv = null, normals = null }) {
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  if (uv) geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  if (normals) geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3))
  geometry.setIndex(new THREE.BufferAttribute(indices, 1))
  if (uv && !normals) geometry.computeVertexNormals()
  return geometry
}

const eyeGeometry = (part) => meshPartGeometry({ ...part, positions: part.local })

const poseKey = (poses) => EYE_SIDES.map((side) => poses[side].rotation.join()).join('|')

export default function EyeTestScene({ assets, controller, headBase, onSystem, onHeadStatus, onReadout }) {
  const { camera, scene, gl, controls } = useThree()
  const groups = useRef({})
  const arrows = useRef({})
  const targetMarkers = useRef({})
  const systemRef = useRef(null)
  const lastPoseKey = useRef('')
  const { mesh } = assets

  // The same pipeline the live avatar mounts, with the panel's controller injected.
  useEffect(() => {
    const spark = new SparkRenderer({ renderer: gl, ...SPARK_TRAINING_BLUR })
    scene.add(spark)

    let disposed = false
    let mounted = null
    onSystem?.({ status: 'loading' })
    createEyeSystem({ scene, camera, assets, controller, controls, lookAtCamera: false })
      .then((system) => {
        if (disposed) { system.dispose(); return }
        mounted = system
        systemRef.current = system
        lastPoseKey.current = ''
        window.__EYE_SYSTEM__ = system
        onSystem?.({ status: 'ready', system })
      })
      .catch((error) => {
        console.error('[eyetest] eye system failed to start', error)
        onSystem?.({ status: 'error', error: error?.message || String(error) })
      })

    return () => {
      disposed = true
      systemRef.current = null
      if (window.__EYE_SYSTEM__ === mounted) delete window.__EYE_SYSTEM__
      mounted?.dispose()
      scene.remove(spark)
      spark.dispose?.()
    }
  }, [assets, controller, camera, controls, gl, scene, onSystem])

  const geometry = useMemo(() => ({
    head: meshPartGeometry({ positions: mesh.head.positions, indices: mesh.head.indices }),
    left: eyeGeometry(mesh.eyes.left),
    right: eyeGeometry(mesh.eyes.right),
  }), [mesh])

  useEffect(() => () => { for (const item of Object.values(geometry)) item.dispose() }, [geometry])

  const arrowObjects = useMemo(() => {
    const built = {}
    for (const side of EYE_SIDES) {
      const shell = mesh.eyes[side]
      built[side] = new THREE.ArrowHelper(
        new THREE.Vector3(...shell.axisOptical),
        new THREE.Vector3(...shell.pivot),
        ARROW_LENGTH, ARROW_COLOR[side], ARROW_LENGTH * 0.25, ARROW_LENGTH * 0.15)
    }
    return built
  }, [mesh])

  useEffect(() => () => {
    for (const arrow of Object.values(arrowObjects)) {
      arrow.dispose?.()
      arrow.line?.geometry?.dispose()
      arrow.cone?.geometry?.dispose()
    }
  }, [arrowObjects])

  // Pose the groups from the panel's gaze; re-lift only on a pose CHANGE.
  useFrame(() => {
    camera.getWorldPosition(_cameraPosition)
    eyeTestState.cameraPosition = [_cameraPosition.x, _cameraPosition.y, _cameraPosition.z]
    applyGazeRequest(controller)
    const poses = controller.update()

    for (const side of EYE_SIDES) {
      const pose = poses[side]
      const group = groups.current[side]
      if (group) group.quaternion.set(...quaternion.toXYZW(pose.rotation))
      const arrow = arrows.current[side]
      if (arrow) {
        arrow.visible = eyeTestState.show.arrows
        arrow.setDirection(_direction.set(...pose.targetDirection))
      }
      const marker = targetMarkers.current[side]
      if (marker) {
        const pivot = mesh.eyes[side].pivot
        marker.visible = eyeTestState.show.arrows
        marker.position.set(
          pivot[0] + pose.targetDirection[0] * ARROW_LENGTH * 1.4,
          pivot[1] + pose.targetDirection[1] * ARROW_LENGTH * 1.4,
          pivot[2] + pose.targetDirection[2] * ARROW_LENGTH * 1.4)
      }
    }
    onReadout?.(poses)

    const system = systemRef.current
    if (!system) return
    system.splatMesh.mesh.visible = eyeTestState.show.splats
    if (!eyeTestState.show.splats) return
    const key = poseKey(poses)
    if (key !== lastPoseKey.current) {
      lastPoseKey.current = key
      void system.relift()
      return
    }
    // Same pose, so only the view can change the render; the system's own epsilon decides.
    system.syncCamera()
  })

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[0.3, 1.8, 0.6]} intensity={1.2} />

      <mesh geometry={geometry.head} visible={eyeTestState.show.headWire}>
        <meshBasicMaterial color={0x6c7480} wireframe transparent opacity={0.55} />
      </mesh>

      {headBase && <NeutralBranchMount base={headBase} onStatus={onHeadStatus} />}

      {EYE_SIDES.map((side) => (
        <group key={side} ref={(object) => { groups.current[side] = object }} position={mesh.eyes[side].pivot}>
          <mesh geometry={geometry[side]} visible={eyeTestState.show.eyeMesh}>
            <meshNormalMaterial flatShading={false} />
          </mesh>
          {eyeTestState.show.eyePoints && (
            <points geometry={geometry[side]}>
              <pointsMaterial color={0xffffff} size={0.0004} sizeAttenuation />
            </points>
          )}
        </group>
      ))}

      {EYE_SIDES.map((side) => (
        <primitive key={`arrow:${side}`} object={arrowObjects[side]}
          ref={(object) => { arrows.current[side] = object }} />
      ))}

      {EYE_SIDES.map((side) => (
        <mesh key={`target:${side}`} ref={(object) => { targetMarkers.current[side] = object }}>
          <sphereGeometry args={[0.0025, 12, 8]} />
          <meshBasicMaterial color={ARROW_COLOR[side]} />
        </mesh>
      ))}

      {eyeTestState.show.pivots && EYE_SIDES.map((side) => (
        <mesh key={`pivot:${side}`} position={mesh.eyes[side].pivot}>
          <sphereGeometry args={[0.0012, 10, 8]} />
          <meshBasicMaterial color={0xff2d55} />
        </mesh>
      ))}
    </>
  )
}
